import React, { useState } from "react";
import { Phone, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { toast } from "sonner";
import api from "../../lib/apiClient";

// PhoneChangeCard — Twilio Verify re-flow for an authenticated user.
// Two-step UX: (1) enter a new E.164 number → server starts a Verify,
// (2) enter the 6-digit OTP → server confirms + swaps `phone_e164`.
// Reuses the existing /auth/sms/change-phone/{send,confirm} endpoints
// which share Twilio rails with the sign-in flow (same per-IP +
// per-phone + budget caps).
const PhoneChangeCard = ({ user, refreshUser }) => {
  const [stage, setStage] = useState("idle"); // idle → sent → done
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Mask all but the trailing 4 digits — never render the full number
  // in case the user is screen-sharing with support.
  const maskedCurrent = user?.phone_e164
    ? `${user.phone_e164.slice(0, 3)}••• ••• ${user.phone_e164.slice(-4)}`
    : null;

  const handleSend = async (e) => {
    e.preventDefault();
    const phone = newPhone.trim();
    if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
      toast.error("Use E.164 format (e.g. +14155552671).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/sms/change-phone/send", { new_phone_e164: phone });
      setStage("sent");
      toast.success("Code sent. Check your new number.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the code.");
    }
    setBusy(false);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Enter the 6-digit code from your SMS.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/sms/change-phone/confirm", {
        new_phone_e164: newPhone.trim(),
        code: code.trim(),
      });
      setStage("done");
      setCode("");
      toast.success("Phone number updated.");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify the code.");
    }
    setBusy(false);
  };

  const reset = () => {
    setStage("idle");
    setNewPhone("");
    setCode("");
  };

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="phone-change-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Phone className="h-5 w-5 text-emerald-400" /> Phone number
        </CardTitle>
        <CardDescription className="text-gray-400">
          {maskedCurrent ? (
            <>
              Current: <span className="text-emerald-300 font-mono" data-testid="phone-current-masked">{maskedCurrent}</span>
              {user?.phone_verified && <span className="ml-2 text-emerald-400/70 text-xs">✓ verified</span>}
            </>
          ) : (
            <>No phone number on file. Add one to enable SMS sign-in.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {stage === "done" && (
          <div className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="phone-change-done">
            <Check className="h-4 w-4" /> Updated. SMS sign-in now uses the new number.
            <Button size="sm" onClick={reset} className="ml-auto bg-gray-800 hover:bg-gray-700">Change again</Button>
          </div>
        )}

        {stage === "idle" && (
          <form onSubmit={handleSend} className="space-y-2 max-w-sm">
            <Label className="text-gray-300 text-sm">
              {maskedCurrent ? "Update to a new number" : "Add a phone number"}
            </Label>
            <Input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+14155552671"
              className="bg-gray-800 border-gray-700 text-white font-mono"
              data-testid="phone-change-new-input"
            />
            <p className="text-xs text-gray-500">E.164 format — start with + and country code.</p>
            <Button
              type="submit"
              disabled={busy || !newPhone}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="phone-change-send-btn"
            >
              {busy ? "Sending..." : "Send verification code"}
            </Button>
          </form>
        )}

        {stage === "sent" && (
          <form onSubmit={handleConfirm} className="space-y-2 max-w-sm">
            <Label className="text-gray-300 text-sm">
              Enter the 6-digit code we just sent to <span className="font-mono text-emerald-300">{newPhone}</span>
            </Label>
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="bg-gray-800 border-gray-700 text-white text-center tracking-[0.4em] text-lg font-mono"
              data-testid="phone-change-code-input"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={busy || code.length < 4}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="phone-change-confirm-btn"
              >
                {busy ? "Verifying..." : "Verify & swap"}
              </Button>
              <Button
                type="button"
                onClick={reset}
                className="bg-gray-800 hover:bg-gray-700"
                data-testid="phone-change-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default PhoneChangeCard;
